// The info card that floats above a selected room: building name, room number,
// and a GO button that opens the routing prompt. Like floorSwitchOverlay, it's
// a raw google.maps.OverlayView so it pans/zooms anchored to the room.

/**
 * @param map       google.maps.Map
 * @param position  {lat, lng} — the room centroid to anchor above
 * @param title     e.g. "Biological Sciences"
 * @param subtitle  e.g. "Room 100"
 * @param roomType  e.g. "Lecture Hall" (from the reference spreadsheet) or null
 * @param onGo      GO button handler (opens routing)
 * @param onClose   ✕ handler (clears the selection)
 * @returns {{ remove: () => void }}
 */
export function createRoomInfoOverlay(map, position, { title, subtitle, roomType, onGo, onClose }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);

  class RoomInfoOverlay extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = 'pfa-roomcard';
      // Room type from the reference spreadsheet, when known.
      const typeBit = roomType ? `<span class="pfa-roomcard-type">${escapeHtml(roomType)}</span>` : '';
      div.innerHTML =
        `<button class="pfa-roomcard-close" aria-label="Close">✕</button>` +
        `<div class="pfa-roomcard-title">${escapeHtml(title)}</div>` +
        (subtitle ? `<div class="pfa-roomcard-sub">${escapeHtml(subtitle)}</div>` : '') +
        (typeBit ? `<div class="pfa-roomcard-meta">${typeBit}</div>` : '') +
        `<button class="pfa-roomcard-go">Find Route</button>`;
      div.querySelector('.pfa-roomcard-go').addEventListener('click', e => { e.stopPropagation(); onGo?.(); });
      div.querySelector('.pfa-roomcard-close').addEventListener('click', e => { e.stopPropagation(); onClose?.(); });
      ['mousedown', 'touchstart', 'pointerdown', 'dblclick'].forEach(t =>
        div.addEventListener(t, e => e.stopPropagation()));
      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }

  const overlay = new RoomInfoOverlay();
  overlay.setMap(map);
  return { remove: () => overlay.setMap(null) };
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
